/**
 * <lv-cart-drawer> — Side cart for the landing page.
 *
 * Intercepts submit on the form named by data-form, sets the cart to exactly the
 * chosen offer (one line of the checked variant, other offer variants removed),
 * then fills and opens the inner <dialog>.
 *
 * Attributes:
 *   data-form         id of the offer form (radios name="id" with data-label/data-qty)
 *   data-update-url   cart update endpoint (Liquid routes.cart_update_url + '.js')
 *   data-shipping     delivery fee in minor units (cents), shown and added to total
 *   data-currency     currency suffix shown after amounts (e.g. "Kz")
 * Slots: [data-item-title] [data-item-sub] [data-item-price] [data-subtotal]
 *        [data-shipping-amount] [data-total]; [data-close] buttons close the dialog.
 */
class LvCartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.form = document.getElementById(this.dataset.form);
    this.shipping = Number(this.dataset.shipping || 0);
    if (!this.form || !this.dialog) return;

    this.onSubmit = (event) => this.handleSubmit(event);
    this.form.addEventListener('submit', this.onSubmit);

    this.querySelectorAll('[data-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.dialog.close());
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('submit', this.onSubmit);
  }

  format(cents) {
    const value = Math.round(cents / 100);
    return value.toLocaleString('pt-PT').replace(/\s|\u202f|\u00a0/g, '.') + ' ' + (this.dataset.currency || '');
  }

  async handleSubmit(event) {
    const checked = this.form.querySelector('input[name="id"]:checked');
    if (!checked) return;
    event.preventDefault();

    const updates = {};
    this.form.querySelectorAll('input[name="id"]').forEach((input) => {
      updates[input.value] = input === checked ? 1 : 0;
    });

    try {
      const response = await fetch(this.dataset.updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error('cart update failed');
      const cart = await response.json();
      this.render(checked, cart);
      this.dialog.showModal();
    } catch (error) {
      // Fall back to the native form post, which lands on the cart page.
      this.form.removeEventListener('submit', this.onSubmit);
      this.form.submit();
    }
  }

  render(checked, cart) {
    const subtotal = cart.total_price;
    const set = (selector, text) => {
      const el = this.querySelector(selector);
      if (el) el.textContent = text;
    };
    set('[data-item-title]', checked.dataset.label || '');
    set('[data-item-sub]', checked.dataset.sub || '');
    set('[data-item-price]', this.format(subtotal));
    set('[data-subtotal]', this.format(subtotal));
    set('[data-shipping-amount]', this.format(this.shipping));
    set('[data-total]', this.format(subtotal + this.shipping));
  }
}

if (!customElements.get('lv-cart-drawer')) customElements.define('lv-cart-drawer', LvCartDrawer);
